import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  computed,
  signal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

import { BestellungArtikel } from '../../models/bestellposition.model';

type WarengruppeSortKey =
  | 'nummer'
  | 'name'
  | 'preis'
  | 'vorschlag'
  | 'menge'
  | 'einheit'
  | 'pkg'
  | 'min'
  | 'max';

type SortDirection = 'asc' | 'desc';

@Component({
  selector: 'app-warengruppen-tab',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './warengruppen-tab.component.html',
  styleUrl: './warengruppen-tab.component.scss',
})
export class WarengruppenTabComponent {
  private readonly artikelSignal = signal<BestellungArtikel[]>([]);
  private readonly searchTermSignal = signal('');

  protected readonly sortKey =
    signal<WarengruppeSortKey | null>(null);
  protected readonly sortDirection = signal<SortDirection>('asc');

  protected readonly skeletonGroups = Array.from({ length: 3 });
  protected readonly skeletonRows = Array.from({ length: 4 });

  @Input()
  set artikel(value: BestellungArtikel[]) {
    this.artikelSignal.set(value);
  }

  @Input()
  set searchTerm(value: string) {
    this.searchTermSignal.set(value);
  }

  @Input()
  isEditMode = false;

  @Input()
  activeArtikelNumber: string | null = null;

  @Input()
  isLoading = false;

  @Input()
  getMengeError: (
    artikel: BestellungArtikel
  ) => string | null = () => null;

  @Input()
  getQuantityInputStep: (
    artikel: BestellungArtikel
  ) => string = () => '1';

  @Input()
  getInputMin: (
    artikel: BestellungArtikel
  ) => number = () => 1;

  @Input()
  getInputMax: (
    artikel: BestellungArtikel
  ) => number | null = () => null;

  @Input()
  getActualQuantity: (
    artikel: BestellungArtikel
  ) => number = (artikel) => artikel.menge ?? 0;

  @Input()
  getQuantityCalculationLabel: (
    artikel: BestellungArtikel
  ) => string | null = () => null;

  @Output()
  readonly mengeDecreased = new EventEmitter<string>();

  @Output()
  readonly mengeIncreased = new EventEmitter<string>();

  @Output()
  readonly mengeUpdated = new EventEmitter<{
    artikelNummer: string;
    event: Event;
  }>();

  @Output()
  readonly artikelSelected = new EventEmitter<string>();

  @Output()
  readonly quantityEnterPressed = new EventEmitter<Event>();

  protected readonly filteredArtikel = computed(() => {
    const term = this.searchTermSignal()
      .trim()
      .toLowerCase();

    if (!term) {
      return this.artikelSignal();
    }

    return this.artikelSignal().filter(
      (item) =>
        item.name.toLowerCase().includes(term) ||
        item.beschreibung.toLowerCase().includes(term) ||
        item.nummer.toLowerCase().includes(term)
    );
  });

  protected readonly warengruppen = computed(() => {
    const groups = new Map<string, BestellungArtikel[]>();

    for (const item of this.filteredArtikel()) {
      const groupName =
        item.beschreibung.split('•')[0]?.trim() ||
        'Ohne Warengruppe';

      if (!groups.has(groupName)) {
        groups.set(groupName, []);
      }

      groups.get(groupName)?.push(item);
    }

    return Array.from(groups.entries()).map(
      ([name, artikel]) => {
        const sortedArtikel = this.sortArtikel(artikel);

        const totalValue = sortedArtikel.reduce(
          (sum, item) => {
            return (
              sum +
              item.preis *
              this.getActualQuantity(item)
            );
          },
          0
        );

        return {
          name,
          artikelCount: sortedArtikel.length,
          artikel: sortedArtikel,
          total: new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
          }).format(totalValue),
        };
      }
    );
  });

  protected onMengeInput(
    artikelNummer: string,
    event: Event
  ): void {
    this.mengeUpdated.emit({
      artikelNummer,
      event,
    });
  }

  protected setSort(
    key: WarengruppeSortKey
  ): void {
    const currentKey =
      this.sortKey();

    // Neue Spalte:
    // Backend-Reihenfolge -> ASC
    if (currentKey !== key) {
      this.sortKey.set(key);
      this.sortDirection.set('asc');
      return;
    }

    // Gleiche Spalte:
    // ASC -> DESC
    if (this.sortDirection() === 'asc') {
      this.sortDirection.set('desc');
      return;
    }

    // Gleiche Spalte:
    // DESC -> Backend-Reihenfolge
    this.sortKey.set(null);
    this.sortDirection.set('asc');
  }

  protected isSortActive(
    key: WarengruppeSortKey
  ): boolean {
    return this.sortKey() === key;
  }

  protected getSortIcon(
    key: WarengruppeSortKey
  ): string {
    if (!this.isSortActive(key)) {
      return 'unfold_more';
    }

    return this.sortDirection() === 'asc'
      ? 'keyboard_arrow_up'
      : 'keyboard_arrow_down';
  }

  private sortArtikel(
    items: BestellungArtikel[]
  ): BestellungArtikel[] {
    const key = this.sortKey();

    // Ohne aktive Sortierung bleibt die
    // Reihenfolge des Backends erhalten.
    if (key === null) {
      return [...items];
    }

    const direction =
      this.sortDirection();

    return [...items].sort((a, b) => {
      const result =
        this.compareArtikelValue(
          a,
          b,
          key
        );

      return direction === 'asc'
        ? result
        : -result;
    });
  }

  private compareArtikelValue(
    a: BestellungArtikel,
    b: BestellungArtikel,
    key: WarengruppeSortKey
  ): number {
    switch (key) {
      case 'nummer':
        return this.compareText(
          a.nummer,
          b.nummer
        );

      case 'name':
        return this.compareText(
          a.name,
          b.name
        );

      case 'preis':
        return this.compareNumber(
          a.preis,
          b.preis
        );

      case 'vorschlag':
        return this.compareNumber(
          a.vorschlag,
          b.vorschlag
        );

      case 'menge':
        return this.compareNumber(
          a.menge ?? a.vorschlag,
          b.menge ?? b.vorschlag
        );

      case 'einheit':
        return this.compareText(
          a.einheit,
          b.einheit
        );

      case 'pkg':
        return this.compareNumber(
          Number(a.pkg),
          Number(b.pkg)
        );

      case 'min':
        return this.compareNullableNumber(
          a.min,
          b.min
        );

      case 'max':
        return this.compareNullableNumber(
          a.max,
          b.max
        );

      default:
        return 0;
    }
  }

  private compareText(
    a: string,
    b: string
  ): number {
    return a.localeCompare(b, 'de', {
      numeric: true,
      sensitivity: 'base',
    });
  }

  private compareNumber(
    a: number,
    b: number
  ): number {
    return a - b;
  }

  private compareNullableNumber(
    a: number | null,
    b: number | null
  ): number {
    if (a === null && b === null) {
      return 0;
    }

    if (a === null) {
      return 1;
    }

    if (b === null) {
      return -1;
    }

    return a - b;
  }
}